import { navDB } from "./db";
import { lc, tokens } from "./helpers";
import type { SearchRecord } from "./types";

function scoreWithPage(
  label: string,
  page: string,
  breadcrumb: string | undefined,
  tags: string[] | undefined,
  qTokens: string[] = []
) {
  const L = lc(label);
  const P = lc(page);
  const B = lc(breadcrumb || "");
  const Tg = (tags || []).map(lc);

  let s = 0;
  for (const t of qTokens) {
    // 1) Item label — strongest
    if (L.includes(t)) s += 10;
    if (L.startsWith(t)) s += 5;

    // 2) Tags — strong
    if (Tg.includes(t)) s += 6;
    if (Tg.some((x) => x.startsWith(t))) s += 3;

    // 3) Page name — weakest
    if (P.includes(t)) s += 1;
    if (P.startsWith(t)) s += 0.5;
  }

  // Shallow breadcrumb (support both " / " and " › ")
  const slashDepth = (B.match(/\//g) || []).length;
  const arrowDepth = (B.match(/›/g) || []).length;
  const depth = Math.max(slashDepth, arrowDepth);
  s += Math.max(0, 5 - depth);

  return s;
}

// Intersect arrays by id (used for AND logic across tokens)
function intersectById<T extends { id: string }>(lists: T[][]): T[] {
  if (!lists.length) return [];
  const map = new Map<string, T>(lists[0].map((x) => [x.id, x]));
  for (let i = 1; i < lists.length; i++) {
    const set = new Set(lists[i].map((x) => x.id));
    for (const id of Array.from(map.keys())) {
      if (!set.has(id)) map.delete(id);
    }
  }
  return Array.from(map.values());
}

/** Fetch item candidates with prefix search + AND logic */
async function fetchItemCandidates(qTokens: string[], moduleRootId?: string) {
  if (qTokens.length === 1) {
    const t = qTokens[0];
    const rows = await navDB.navItems.where("terms").startsWith(t).distinct().toArray();
    return moduleRootId ? rows.filter((r) => r.moduleRootId === moduleRootId) : rows;
  }
  const perToken = await Promise.all(
    qTokens.map((t) => navDB.navItems.where("terms").startsWith(t).distinct().toArray())
  );
  let rows = intersectById(perToken);
  if (moduleRootId) rows = rows.filter((r) => r.moduleRootId === moduleRootId);
  return rows;
}

/** Fetch page candidates with prefix search + AND logic */
async function fetchPageCandidates(qTokens: string[], moduleRootId?: string) {
  if (qTokens.length === 1) {
    const t = qTokens[0];
    const rows = await navDB.navNodes.where("terms").startsWith(t).distinct().toArray();
    const pages = rows.filter((n: any) => n.type === "page");
    return moduleRootId ? pages.filter((r: any) => r.moduleRootId === moduleRootId) : pages;
  }
  const perToken = await Promise.all(
    qTokens.map((t) => navDB.navNodes.where("terms").startsWith(t).distinct().toArray())
  );
  let rows: any[] = intersectById(perToken).filter((n: any) => n.type === "page");
  if (moduleRootId) rows = rows.filter((r: any) => r.moduleRootId === moduleRootId);
  return rows;
}

/* --------------------------------- API ---------------------------------- */
/** Search across all modules stored; optionally filter by a moduleRootId. */
export async function searchAll(query: string, limit = 50, moduleRootId?: string) {
  const qTokens = tokens(query);
  if (!qTokens.length) return [];

  // gather candidates (prefix + AND)
  const [itemCands, pageCands] = await Promise.all([
    fetchItemCandidates(qTokens, moduleRootId),
    fetchPageCandidates(qTokens, moduleRootId),
  ]);

  const itemResults: SearchRecord[] = itemCands.map((it: any) => {
    const crumbs = (it.breadcrumb || "").split(" › ");

    const pageName = crumbs[crumbs.length - 1] || "";
    const moduleName = crumbs[0] || "";

    return {
      id: it.id,
      route: it.route,
      page: pageName,
      label: it.label,
      tags: it.tags || [],
      module: moduleName,
      anchor: it.key,
      breadcrumb: it.breadcrumb || "",
      score: 0,
      url: it.url,
      drawerType: it.drawerType,
      drawerProps: it.drawerProps,
    };
  });

  const pageResults: SearchRecord[] = pageCands.map((n: any) => {
    const crumbs = (n.breadcrumb || "").split(" › ");
    const moduleName = crumbs[0] || "";
    return {
      id: n.id,
      route: n.route || "",
      page: n.title,
      label: n.title,
      tags: n.tags || [],
      module: moduleName,
      anchor: undefined,
      breadcrumb: n.breadcrumb || "",
      score: 0,
      url: n.route || "",
    };
  });

  const results = [...itemResults, ...pageResults]
    .map((r) => ({
      ...r,
      score: scoreWithPage(r.label, r.page, r.breadcrumb, r.tags, qTokens),
    }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return results;
}
